import type { HazardousGoodsProductSelection } from '@utils/MultiPageSelectionHelper';

export interface Pagination {
  count: number;
  page: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/** Interface to create values for status select field. */
export interface GsmProductStatusDisplayValue {
  /** Displayed name. */
  displayName: string;
  /** Value sent to backend. */
  displayValue: GsmProductStatus;
}

export enum GsmProductStatus {
  NEW = 'NEW',
  CHANGED = 'CHANGED',
  READ = 'READ',
  INACTIVE = 'INACTIVE',
}

export interface GsmDocument {
  abbreviation?: string;
  url?: string;
}

export interface GsmProduct {
  code: string;
  hazMatNumber: string;
  description: string;
  formattedCode?: string;
  manufacturer?: string;
  clpDocuments?: Array<GsmDocument>;
  clipDocumentText?: string;
  foerchDocuments: Array<GsmDocument>;
  foerchDocumentText?: string;
  pictureUrl?: string;
  synonyms?: string[];
  lastChanged?: number;
  status?: GsmProductStatus;
  customerArticle?: boolean;
  // amount: 0;
}

export interface GsmProductResponse {
  pagination: Pagination;
  results: Array<GsmProduct>;
}

/**
 * Interface for media types.
 * Media types are part of the customer article source.
 * They are used to represent data sheet types.
 */
export interface GsmMediaType {
  code?: string;
  name?: string;
}

/**
 * Interface for filter event triggered by the gsm filters.
 */
export interface GsmFilterEvent {
  /** Letter of alphabetical filtering. */
  letter: string;
  /** Selected manufacturer/producer. */
  producer: string;
  /** Selected data sheet types. */
  dataSheetTypes: GsmMediaType[];
  /** The status selection, e.g. ["NEW", "CHANGED"].*/
  hazMatProductStatus?: GsmProductStatus[];
}

export enum GsmBulkOption {
  DOWNLOAD_DATASHEETS = 'Datenblätter',
  DOWNLOAD_HAZARDOUS_GOODS_LIST = 'Gefahrstoffverzeichnis',
  DOWNLOAD_COLLECTIVE_OPERATING_INSTRUCTIONS = 'Sammel-Betriebsanweisungen',
}

export enum DocumentType {
  PDF = 'PDF',
  ZIP = 'ZIP',
}

export enum SelectionMode {
  EXCLUDED = 'EXCLUDED',
  SELECTED = 'SELECTED',
}

export enum AlertType {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
  DANGER = 'danger',
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  LIGHT = 'light',
  DARK = 'dark',
}

export interface Alert {
  type: AlertType;
  message: string;
}

/**
 * Interface used for an iterable list to represent alerts in template.
 */
export interface GsmFrontendAlert {
  id: number;
  alert: Alert;
}

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface ColumnSortModel {
  sortBy: string;
  sortDirection: SortDirection;
}

export interface GsmSelectionFilter {
  documentTypes?: string;
  onlyCustomerArticle?: boolean;
  hideInactiveArticles?: boolean;
  operatingInstructionCode?: any;
  query?: any;
  workingAreaCode?: string;
  manufacturer?: string;
  wordStarting?: string;
  articleStatus?: GsmProductStatus[];
}

/**
 * Interface for a download process.
 * A document is generated in backend. Via WebSocket we will be notified when
 * a document is ready to download.
 */
export interface GsmDownload {
  /** ID of the download process. Needed to refer to the correct process. */
  processId: string;
  /** The link to the document that is generated in backend. */
  downloadLink: string;
}

/**
 * Interface for event to init sending email.
 */
export interface GsmSendDownloadEmailEvent {
  /** ID of the download process. Needed to refer to the correct process. */
  processId: string;
  /** List of emails where the download should be sent to. */
  emails: string[];
}

/**
 * Interface for download documents event.
 */
export interface GsmDownloadDocumentsEvent {
  /** Chosen bulk option. */
  option: GsmBulkOption;
  /** Only relevant for DataSheets. Format of the download. (e.g. "PDF") */
  format?: DocumentType;
  /** Only relevant for DataSheets. Chosen data sheet types. (e.g. "FOERCH, CLP) */
  dataSheetTypes?: string[];
  /** Currently used filters such as a manufacturer etc. */
  filter?: GsmSelectionFilter;
}

/**
 * Interface for request body that is sent when data sheets should be downloaded.
 */
export interface GsmDownloadDataSheetsBody {
  /** Selection mode and product codes. */
  selection: HazardousGoodsProductSelection;
  /** Format of the download. (e.g. "PDF") */
  format: DocumentType;
  /** Chosen data sheet types. (e.g. "FOERCH, CLP) */
  dataSheets: string[];
  /** Currently used filters such as a manufacturer etc. */
  filter: GsmSelectionFilter;
}

/**
 * Interface for request body that is sent when
 * operating instructions or hazardous goods list should be downloaded.
 */
export interface GsmDownloadBody {
  /** Selection mode and product codes. */
  selection: HazardousGoodsProductSelection;
  /** Currently used filters such as a manufacturer etc. */
  filter: GsmSelectionFilter;
}

export interface GsmDocumentCountBody extends GsmDownloadBody {
  // Optional: comma separated list of document types to be counted
  countedTypes?: string;
}

export enum GsmDocumentCount {
  NONE = 'NONE',
  PARTIAL = 'PARTIAL',
  ALL = 'ALL',
}

export interface GsmDocumentCountResponse {
  result?: GsmDocumentCount;
}
